@if (loading()) {
  <div class="loading-container">
    <oph-loading></oph-loading>
  </div>
} @else {
  @if (challengeView(); as cv) {
    <div class="sample-data-banner" role="status">
      <mat-icon svgIcon="info" class="sample-data-icon"></mat-icon>
      <span>You're viewing sample data</span>
    </div>

    <div class="section-container">
      <div class="section-header">Progress</div>

      <div class="progress-row">
        <!-- Current Cycle -->
        <div class="progress-card">
          <div class="progress-card-title">
            <span class="progress-card-label">Current Cycle</span>
          </div>
          <div class="pill-row">
            @for (item of cv.currentCycle; track $index) {
              <div
                class="pill"
                [class.pill--success]="item.successful && !item.active"
                [class.pill--active]="item.active"
                [class.pill--cycle]="item.successful && !item.active"
                [class.pill--future]="!item.successful && !item.active && (cv.currentCycle | isAfterActive: $index)"
              ></div>
            }
          </div>
        </div>

        <!-- Level -->
        <div class="progress-card">
          <div class="progress-card-title">
            <span class="progress-card-label">Level {{ cv.level.levelNumber }}</span>
            <span class="streak-count">
              <span class="streak-number">{{ cv.level.streakCount }}</span>
              <img class="streak-icon" src="assets/img/icons/challenge-viewer/cv-bolt.svg" alt="Streak Icon" />
            </span>
          </div>
          <div class="pill-row">
            @for (item of cv.level.items; track $index) {
              <div
                class="pill"
                [class.pill--success]="item.successful && !item.active"
                [class.pill--active]="item.active"
                [class.pill--level]="item.successful && !item.active"
                [class.pill--future]="!item.successful && !item.active && (cv.level.items | isAfterActive: $index)"
              ></div>
            }
          </div>
        </div>

        <!-- Coin Count -->
        <div class="coin-card">
          <span class="coin-count">{{ coinCount() | numberAbbreviation }}</span>
          <mat-icon svgIcon="coin-golden" class="coin-icon"></mat-icon>
        </div>
      </div>
    </div>

    <!-- Outline / More Section -->
    <div
      class="section-container outline-more-container"
      [class.outline-has-scroll]="outlineHasScroll()"
      [class.more-has-scroll]="moreHasScroll()"
    >
      <!-- Outline Panel -->
      <div class="outline-panel">
        <div class="panel-title-bar">
          <span class="panel-title">Outline</span>
          <div class="button-group">
            @for (tab of ['tasks', 'metrics', 'targets']; track tab) {
              <button class="group-btn" [class.active]="outlineTab() === tab" (click)="selectOutlineTab($any(tab))">
                {{ tab === 'tasks' ? 'Tasks' : tab === 'metrics' ? 'Metrics' : 'Targets' }}
              </button>
            }
          </div>
        </div>

        <div class="panel-content-wrapper oph-scroll-bar" #outlineScroll>
          <div
            class="panel-content"
            [class.slide-left]="outlineDirection() === 'left'"
            [class.slide-right]="outlineDirection() === 'right'"
          >
            @switch (outlineTab()) {
              <!-- Tasks Tab -->
              @case ('tasks') {
                <div class="item-list">
                  @for (task of cv.tasks; track task._id) {
                    <div class="item-row">
                      <div class="item-icon" [class.item-icon--salvage]="task.isSalvage">
                        <mat-icon [svgIcon]="task.isSalvage ? 'cv-salvage' : 'cv-task'"></mat-icon>
                      </div>
                      <span class="item-name">{{ task.label }}</span>
                      @if (task.isSalvage) {
                        <div class="salvage-chip">
                          <mat-icon svgIcon="cv-salvage" class="salvage-chip-icon"></mat-icon>
                          <span>Salvage</span>
                        </div>
                      } @else {
                        <cb-schedule-chip [type]="task.schedule | scheduleChipType" />
                      }
                    </div>
                  }
                </div>
              }

              <!-- Metrics Tab -->
              @case ('metrics') {
                <div class="item-list">
                  @for (metric of cv.metrics; track $index) {
                    <div class="item-row">
                      <div class="item-icon item-icon--metric">
                        <mat-icon svgIcon="tracker"></mat-icon>
                      </div>
                      <span class="item-name">{{ metric.name }}</span>
                      @switch (metric.type) {
                        @case (TrackerMetricTypes.Duration) {
                          <div class="metric-display metric-duration">
                            <div class="metric-box">
                              <span>{{ metric.typeOption || 'min' }}</span>
                            </div>
                          </div>
                        }
                        @case (TrackerMetricTypes.Boolean) {
                          <div class="metric-display metric-boolean">
                            <div class="metric-box bool-box">
                              <span class="bool-label">yes</span>
                              <div class="bool-divider"></div>
                              <span class="bool-label">no</span>
                            </div>
                          </div>
                        }
                        @default {
                          <div class="metric-display metric-numeric">
                            <div class="metric-box">
                              <span>num</span>
                            </div>
                          </div>
                        }
                      }
                    </div>
                  }
                </div>
              }

              <!-- Targets Tab -->
              @case ('targets') {
                <div class="item-list">
                  @for (metric of cv.metrics; track $index) {
                    @if (metric.target) {
                      <div class="item-row item-row--target">
                        <div class="item-icon item-icon--target">
                          <mat-icon svgIcon="target"></mat-icon>
                        </div>
                        <div class="target-text">
                          <span class="item-name">{{ metric.target.targetName || metric.name }}</span>
                          <span class="target-description">{{ metric | cbTargetDisplay }}</span>
                        </div>
                      </div>
                    }
                  }
                </div>
              }
            }
          </div>
        </div>
      </div>

      <!-- Vertical Divider -->
      <div class="vertical-divider"></div>

      <!-- More Panel -->
      <div class="more-panel">
        <div class="panel-title-bar">
          <span class="panel-title">More</span>
          <div class="button-group">
            @for (tab of ['milestones', 'notifications']; track tab) {
              <button class="group-btn" [class.active]="moreTab() === tab" (click)="selectMoreTab($any(tab))">
                {{ tab === 'milestones' ? 'Milestones' : 'Notifications' }}
              </button>
            }
          </div>
        </div>

        <div class="panel-content-wrapper oph-scroll-bar" #moreScroll>
          <div
            class="panel-content"
            [class.slide-left]="moreDirection() === 'left'"
            [class.slide-right]="moreDirection() === 'right'"
          >
            @switch (moreTab()) {
              <!-- Milestones Tab -->
              @case ('milestones') {
                <div class="item-list">
                  @for (milestone of cv.milestones; track milestone._id) {
                    <div class="item-row item-row--milestone">
                      <div class="item-icon item-icon--milestone">
                        <mat-icon svgIcon="cv-milestone"></mat-icon>
                      </div>
                      <div class="milestone-text">
                        <span class="item-name">{{ milestone.name }}</span>
                        <span class="milestone-description">{{ milestone.description }}</span>
                      </div>
                      @if (milestone.successCount > 0) {
                        <div class="milestone-badge">
                          <mat-icon svgIcon="cv-task" class="milestone-badge-icon"></mat-icon>
                          <span class="milestone-badge-count">{{ milestone.successCount }}</span>
                        </div>
                      }
                    </div>
                  }
                </div>
              }

              <!-- Notifications Tab -->
              @case ('notifications') {
                <div class="item-list">
                  @for (notification of cv.notifications; track $index) {
                    <div class="item-row item-row--notification">
                      <div
                        class="item-icon"
                        [class.item-icon--notification-active]="notification.active"
                        [class.item-icon--notification-inactive]="!notification.active"
                      >
                        <mat-icon svgIcon="cv-alert"></mat-icon>
                      </div>
                      <div class="notification-content">
                        <div class="notification-header">
                          <span class="item-name">{{ notification.time | formatAlertTime }}</span>
                          <div class="notification-days">
                            @for (day of notification | notificationDays: cycleLength(); track $index) {
                              <div
                                class="day-badge"
                                [class.day-badge--selected]="day.selected"
                                [class.day-badge--active]="notification.active"
                                [class.day-badge--inactive]="!notification.active"
                              >
                                <span>{{ day.label }}</span>
                              </div>
                            }
                          </div>
                        </div>
                        <span class="notification-message">{{ notification.message }}</span>
                      </div>
                      <oph-slide-toggle
                        [checked]="notification.active"
                        (toggle)="onToggleAlert(notification, $event)"
                      />
                    </div>
                  }
                </div>
              }
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Cycles -->
    @for (cycle of sortedCycles(); track cycle.cycleNumber) {
      <div class="cycle-card">
        <div class="cycle-card-header">
          <div class="cycle-title-row">
            <span class="cycle-number">Cycle {{ cycle.cycleNumber }}</span>
            <div
              class="cycle-badge"
              [class.cycle-badge--success]="cycle.percentComplete >= cycle.cycleSuccessThreshold"
              [class.cycle-badge--pending]="cycle.percentComplete < cycle.cycleSuccessThreshold"
            >
              @if (cycle.percentComplete >= cycle.cycleSuccessThreshold) {
                <svg class="badge-check" width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M2.5 6.5L5.5 9.5L10.5 3.5"
                    stroke="#1b9f2b"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
              <span class="badge-label">{{ cycle.cycleSuccessThreshold | thresholdLabel: thresholdTabs }}</span>
            </div>
          </div>

          @if ($first) {
            <div class="threshold-tabs">
              @for (tab of thresholdTabs; track tab.value) {
                <button
                  [disabled]="true"
                  class="threshold-tab"
                  [class.active]="cycle.cycleSuccessThreshold === tab.value"
                  (click)="onThresholdChange(cycle, tab.value)"
                >
                  {{ tab.label }}
                </button>
              }
            </div>
          }
        </div>

        <div class="cycle-percent">
          <span class="percent-number">{{ cycle.percentComplete }}</span>
          <span class="percent-sign">%</span>
        </div>

        <div class="cycle-metrics-wrapper">
          <div class="cycle-metrics-scroll" (scroll)="onMetricsScroll($event)">
            @for (item of cycle | cycleDisplayItems; track item.metricId) {
              <div class="cycle-metric-card">
                <div class="metric-card-top">
                  <img
                    class="metric-icon"
                    [src]="'assets/img/icons/color-icons/' + (item.icon || 'memo') + '.svg'"
                    [alt]="item.name"
                  />
                  <div class="metric-meter">
                    <svg class="meter-svg" viewBox="0 0 26 26" aria-hidden="true">
                      <circle class="meter-track" cx="13" cy="13" r="10" />
                      <circle
                        class="meter-progress"
                        cx="13"
                        cy="13"
                        r="10"
                        [style.stroke]="item.color"
                        [style.stroke-dasharray]="circumference"
                        [style.stroke-dashoffset]="item.score | meterDashOffset: circumference"
                      />
                    </svg>
                    <span class="meter-value">{{ item.score > 100 ? 100 : item.score }}</span>
                  </div>
                </div>

                <oph-metric-bar-chart [dailyScores]="item.dailyScores" [color]="item.color" [height]="80" />

                <div class="metric-card-footer">
                  <span class="metric-name">{{ item.name }}</span>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }
  }
}
